import React, { forwardRef } from "react";
import DiamondShapeSVG from "../DiamondShapeSVG";

const fmtVal = (v) =>
  typeof v === "boolean" ? (v ? "Yes" : "No") : v === null || v === undefined ? "—" : String(v);

const JewelleryShowcase = forwardRef(({
  product,
  buyer,
  cardScale = 100,
  productImageRef,
  visibleFields = {},
  cssVars = {},
  forExport = false,
  activeTheme,
  activeFontPair,
  cardBgColor,
  cardTextColor,
}, ref) => {
  const isVisible = (key) => visibleFields[key] !== false;

  // ── resolve fields from both carddemo shape AND real seller product shape ──
  const productName  = product?.name || product?.productName || "Fine Jewellery";
  const segment      = product?.segment  || "";
  const category     = product?.category || "";

  // Images: support both image_url[] (carddemo) and images[{url}] (seller product)
  const productImage = (() => {
    if (Array.isArray(product?.image_url) && product.image_url[0]) return product.image_url[0];
    if (typeof product?.image_url === "string") return product.image_url;
    if (Array.isArray(product?.images) && product.images[0]) {
      return typeof product.images[0] === "string" ? product.images[0] : product.images[0].url || "";
    }
    return "";
  })();

  // avg gross wt — from lotDetails first, then root grossWt (shown as "Weight")
  const avgGrossWt = product?.lotDetails?.[0]?.avgGrossWt || product?.grossWt || "";

  const price = product?.pricing?.retail || product?.attributes?.retail_price || 0;
  const formatPrice = (v) =>
    new Intl.NumberFormat("en-IN", {
      style: "currency", currency: "INR",
      minimumFractionDigits: 0, maximumFractionDigits: 0,
    }).format(v);

  // Build the display attribute grid:
  // Use product.attributes if present (carddemo/normalized path),
  // otherwise build from real seller fields.
  const hasAttributes = product?.attributes && Object.keys(product.attributes).some(
    (k) => k !== "retail_price" && product.attributes[k] !== undefined && product.attributes[k] !== ""
  );

  const rawAttrEntries = hasAttributes
    ? Object.entries(product.attributes)
        .filter(([k]) => k !== "retail_price" && k !== "Segment" && k !== "Category")
        .filter(([k]) => isVisible(k))
    : [];

  // Core fields shown on poster — only avgGrossWt as "Weight", no wastage, no avgNetWt
  const coreFields = [
    segment      && ["Segment",  segment],
    category     && ["Category", category],
    avgGrossWt   && ["Weight",   `${parseFloat(avgGrossWt).toFixed(3)} g`],
    product?.purity && ["Purity", `${product.purity}%`],
    product?.specification && product.specification !== "PLANE" && ["Specification", product.specification],
    (product?.styleType || product?.finishType) && ["Style", product.styleType || product.finishType],
  ].filter(Boolean);

  // Merge: core fields first, then any extra attributes
  const segmentAttrEntries = [...coreFields, ...rawAttrEntries];

  const rootStyle = {
    transform: forExport ? "scale(1)" : `scale(${cardScale / 100})`,
    transformOrigin: "center top",
    width: "420px",
    minHeight: "560px",
    padding: 0,
    position: "relative",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    ...cssVars,
  };

  return (
    <div ref={ref} style={rootStyle} className="card-preview-wrapper layout-editorial-catalogue">

      {/* ── Background ── */}
      <div style={{ position: "absolute", inset: 0, background: "var(--theme-bg)", zIndex: 0 }} />

      {/* ── Decorative: faded circle top-right ── */}
      <div style={{
        position: "absolute", top: -80, right: -80,
        width: 240, height: 240, borderRadius: "50%",
        background: "var(--theme-accent)", opacity: 0.08, zIndex: 1,
      }} />
      {/* ── Decorative: small circle bottom-left ── */}
      <div style={{
        position: "absolute", bottom: -50, left: -50,
        width: 160, height: 160, borderRadius: "50%",
        background: "var(--theme-accent)", opacity: 0.1, zIndex: 1,
      }} />
      {/* ── Accent top bar ── */}
      <div style={{
        position: "absolute", top: 0, left: 0, right: 0, height: 4,
        background: "var(--theme-accent)", zIndex: 2,
      }} />
      {/* ── Corner ornaments ── */}
      {[
        { top: 12, left: 12, borderTop: "2px solid", borderLeft: "2px solid", borderRight: "none", borderBottom: "none" },
        { top: 12, right: 12, borderTop: "2px solid", borderRight: "2px solid", borderLeft: "none", borderBottom: "none" },
        { bottom: 12, left: 12, borderBottom: "2px solid", borderLeft: "2px solid", borderRight: "none", borderTop: "none" },
        { bottom: 12, right: 12, borderBottom: "2px solid", borderRight: "2px solid", borderLeft: "none", borderTop: "none" },
      ].map((s, i) => (
        <div key={i} style={{
          position: "absolute", width: 18, height: 18,
          borderColor: "var(--theme-accent)", zIndex: 3, ...s,
        }} />
      ))}

      {/* ── Content wrapper ── */}
      <div style={{
        position: "relative", zIndex: 4,
        display: "flex", flexDirection: "column",
        padding: "28px 24px 20px", flex: 1,
        fontFamily: "var(--theme-body-font,'Inter',sans-serif)",
      }}>

        {/* ── Header: eyebrow + title ── */}
        <div style={{ textAlign: "center", marginBottom: 14 }}>
          <div style={{
            fontSize: "0.6rem", fontWeight: 700, letterSpacing: "0.25em",
            textTransform: "uppercase", color: "var(--theme-accent)",
            marginBottom: 6,
            fontFamily: "var(--theme-body-font,'Inter',sans-serif)",
          }}>
            ✦ {segment?.toUpperCase() || "JEWELLERY COLLECTION"} ✦
          </div>
          <h1 style={{
            fontFamily: "var(--theme-heading-font,'Playfair Display',serif)",
            fontSize: "1.4rem", fontWeight: 700,
            color: "var(--theme-text)",
            margin: 0, lineHeight: 1.2, letterSpacing: "0.02em",
          }}>
            {productName.toUpperCase()}
          </h1>
          {category && (
            <div style={{
              fontSize: "0.58rem", fontWeight: 600, letterSpacing: "0.15em",
              textTransform: "uppercase", color: "var(--theme-textMuted)",
              marginTop: 3,
              fontFamily: "var(--theme-body-font,'Inter',sans-serif)",
            }}>
              {category}
            </div>
          )}
          <div style={{
            width: 48, height: 2, background: "var(--theme-accent)",
            margin: "10px auto 0",
          }} />
        </div>

        {/* ── Image frame ── */}
        <div style={{
          position: "relative",
          background: "var(--theme-surface)",
          border: "1px solid var(--theme-border)",
          borderRadius: 6, overflow: "hidden",
          marginBottom: 12, aspectRatio: "4 / 3",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <div style={{
            position: "absolute", top: 8, left: 8, right: 8, bottom: 8,
            border: "1px solid", borderColor: "var(--theme-accent)",
            borderRadius: "50% 50% 0 0", opacity: 0.35, pointerEvents: "none",
          }} />
          <img
            ref={productImageRef}
            src={productImage}
            alt={productName}
            crossOrigin="anonymous"
            style={{ width: "100%", height: "100%", objectFit: "contain", position: "relative", zIndex: 1 }}
          />
          {/* corner stars */}
          {["8px,8px", "8px,auto,auto,8px", "auto,8px,8px", "auto,auto,8px,8px"].map((pos, i) => {
            const [top, right, bottom, left] = pos.split(",");
            return (
              <div key={i} style={{
                position: "absolute",
                top: top !== "auto" ? top : undefined,
                right: right !== "auto" ? right : undefined,
                bottom: bottom !== "auto" ? bottom : undefined,
                left: left !== "auto" ? left : undefined,
                color: "var(--theme-accent)", fontSize: "0.75rem", zIndex: 3, opacity: 0.6,
              }}>✦</div>
            );
          })}
        </div>

        {/* ── Spec grid: segment, category, avg gross wt + all attributes ── */}
        {segmentAttrEntries.length > 0 && (
          <div style={{
            display: "grid", gridTemplateColumns: "1fr 1fr",
            gap: "4px 10px",
            padding: "8px 0",
            borderTop: "1px solid var(--theme-border)",
            borderBottom: "1px solid var(--theme-border)",
            marginBottom: 12,
          }}>
            {segmentAttrEntries.map(([k, v]) => (
              <div key={k} style={{ textAlign: "center" }}>
                <div style={{
                  fontSize: "0.52rem", fontWeight: 700, textTransform: "uppercase",
                  letterSpacing: "0.12em", color: "var(--theme-accent)", marginBottom: 2,
                  fontFamily: "var(--theme-body-font,'Inter',sans-serif)",
                }}>
                  {k.replace(/_/g, " ")}
                </div>
                {k === "shape" && product?.segment?.toLowerCase() === "diamond" ? (
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 3 }}>
                    <DiamondShapeSVG shapeName={String(v)} size={28} color="var(--theme-accent)" />
                    <span style={{
                      fontSize: "0.64rem", fontWeight: 700, color: "var(--theme-text)",
                      fontFamily: "var(--theme-heading-font,'Playfair Display',serif)",
                    }}>{fmtVal(v)}</span>
                  </div>
                ) : (
                  <div style={{
                    fontSize: "0.72rem", fontWeight: 700, color: "var(--theme-text)",
                    fontFamily: "var(--theme-heading-font,'Playfair Display',serif)",
                  }}>
                    {fmtVal(v)}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* ── Description ── */}
        {product?.description && (
          <p style={{
            fontSize: "0.68rem", lineHeight: 1.55,
            color: "var(--theme-textMuted)",
            fontFamily: "var(--theme-body-font,'Inter',sans-serif)",
            margin: "0 0 12px", fontStyle: "italic", textAlign: "center",
          }}>
            "{product.description}"
          </p>
        )}

        {/* ── Price row ── */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "center",
          gap: 10, marginBottom: 14,
        }}>
          <div style={{ flex: 1, height: 1, background: "var(--theme-border)" }} />
          <div style={{
            background: "var(--theme-accent)", color: "var(--theme-bg)",
            fontSize: "1rem", fontWeight: 800,
            padding: "6px 20px", borderRadius: 4,
            fontFamily: "var(--theme-heading-font,'Playfair Display',serif)",
            letterSpacing: "0.02em",
          }}>
            {formatPrice(price)}
          </div>
          <div style={{ flex: 1, height: 1, background: "var(--theme-border)" }} />
        </div>

        <div style={{ flex: 1 }} />

        {/* ── Bottom: brand ── */}
        <div style={{
          display: "flex", borderTop: "1px solid var(--theme-border)",
          paddingTop: 10, gap: 8,
        }}>
          {/* Follow us */}
          <div style={{
            flex: 1, display: "flex", alignItems: "center", gap: 8,
            padding: "8px 10px",
            background: "var(--theme-surface)",
            border: "1px solid var(--theme-border)", borderRadius: 4,
          }}>
            {isVisible("logoUrl") && buyer?.logoUrl && (
              <img src={buyer.logoUrl} alt="Logo"
                style={{ width: 24, height: 24, objectFit: "contain", borderRadius: 3 }} />
            )}
            <div>
              <div style={{
                fontSize: "0.6rem", fontWeight: 700, color: "var(--theme-accent)",
                textTransform: "uppercase", letterSpacing: "0.1em",
                fontFamily: "var(--theme-body-font,'Inter',sans-serif)",
              }}>Follow Us</div>
              {isVisible("address") && (
                <div style={{
                  fontSize: "0.52rem", color: "var(--theme-textMuted)",
                  fontFamily: "var(--theme-body-font,'Inter',sans-serif)",
                }}>
                  📍 {buyer?.streetAddress || "123 Jewellers Lane"}
                </div>
              )}
              {isVisible("phone") && (
                <div style={{
                  fontSize: "0.52rem", color: "var(--theme-textMuted)",
                  fontFamily: "var(--theme-body-font,'Inter',sans-serif)",
                }}>
                  📞 {buyer?.transactionalMobile || buyer?.mobile || "+91 98765 43210"}
                </div>
              )}
            </div>
          </div>

          {/* Store name */}
          <div style={{
            flex: 1, display: "flex", flexDirection: "column",
            alignItems: "center", justifyContent: "center",
            padding: "8px 10px",
            background: "var(--theme-surface)",
            border: "1px solid var(--theme-border)", borderRadius: 4,
            gap: 4,
          }}>
            {isVisible("logoUrl") && buyer?.logoUrl && (
              <img src={buyer.logoUrl} alt="Logo"
                style={{ width: 24, height: 24, objectFit: "contain", borderRadius: 3 }} />
            )}
            {isVisible("organizationName") && (
              <div style={{
                fontSize: "0.65rem", fontWeight: 800,
                color: "var(--theme-text)",
                textTransform: "uppercase", letterSpacing: "0.12em",
                textAlign: "center",
                fontFamily: "var(--theme-heading-font,'Playfair Display',serif)",
              }}>
                {(buyer?.organizationName || "YOUR STORE").toUpperCase()}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
});

JewelleryShowcase.displayName = "JewelleryShowcase";
export default JewelleryShowcase;
